import React, { ChangeEvent, useEffect, useState } from "react";
import Page from "components/page/Page";
import { useTranslation } from "react-i18next";
import { DeleteCampaign, GetAudienceCustomers, GetAudienceCustomersExport, GetAudiences, GetEmailCampaigns, IAudience, IAudienceCustomer, IEmailCampaign, PostCampaignDuplicate, PostCustomer, PutAudience, PutPublishEmailCampaign, PutStep1EmailCampaign, PutStep2EmailCampaign, PutStep3EmailCampaign } from "api/rpc/2024-04/facilityAdmin/marketing/emailCampaigns";
import { useDispatch } from "react-redux";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import Radio from "components/radio";
import Select from "components/select/Select";
import Option from "components/select/Option";
import { ButtonNew } from "components/buttonNew";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { useHistory, useParams } from "react-router";
import Accordion from "./Accordion";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import moment from "moment";
import TimePick from "components/timePick/TimePick";
import { downloadCsv, formatDate } from "helpers/Helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import Markdown from "components/markdown/Markdown";
import Sheet from "components/sheet/Sheet";
import AudienceCustomersTable from "./AudienceCustomersTable";
import TextEditor from "components/textEditor/textEditor";
import _ from "lodash";

interface IPageState {
  campaign: IEmailCampaign & { scheduled_date?: string; scheduled_time?: string };
  selectedTab: TabType,
}

enum TabType {
  Details = 0,
  Audience,
  Preview,
}

export default function EditEmailCampaign() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id: campaignId } = useParams<{ id: string }>();
  const isEdit = campaignId !== undefined;
  const [audiences, setAudiences] = useState<IAudience[]>([]);
  const [segmentModalOpen, setSegmentModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [state, setState] = useState<IPageState>({
    campaign: {
      id: 0,
      name: '',
      type: 'email',
      status: 'draft',
      description: '',
      subject: '',
      text_preview: '',
      content: '',
      audience_id: 0,
      from_name: '',
      from_address: '',
      scheduled_type: 'now',
      scheduled_date: formatDate(new Date()),
      scheduled_at: formatDate(new Date()),
    },
    selectedTab: TabType.Details,
  });
  const [counts, setCounts] = useState({
    includedCount: 0,
    excludedCount: 0,
    total: 0,
  });
  const [audienceCustomers, setAudienceCustomers] = useState<IAudienceCustomer[]>([]);

  async function publish() {
    await updateStep3();

    const resp = await PutPublishEmailCampaign({
      id: state.campaign.id,
    }, false);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    dispatch(showSuccess(t('Email campaign updated')));
  }

  async function updateStep1() {
    const resp = await PutStep1EmailCampaign({
      id: state.campaign.id,
      name: state.campaign.name,
      description: state.campaign.description,
    }, false);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return false;
    }

    dispatch(showSuccess(t('Email campaign updated')));
    return true;
  }

  async function updateStep2() {
    const resp = await PutStep2EmailCampaign({
      id: state.campaign.id,
      audience_id: state.campaign.audience_id,

      from_name: state.campaign.from_name,
      from_address: state.campaign.from_address,

      subject: state.campaign.subject,
      text_preview: state.campaign.text_preview,

      scheduled_type: state.campaign.scheduled_type,
      scheduled_recurring_type: state.campaign.scheduled_recurring_type,
      scheduled_at: state.campaign.scheduled_type === 'later' ? state.campaign.scheduled_date + ' ' + state.campaign.scheduled_time : null,
    }, true);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return false;
    }

    dispatch(showSuccess(t('Email campaign updated')));
    return true;
  }

  async function updateStep3() {
    const resp = await PutStep3EmailCampaign({
      id: state.campaign.id,
      content: state.campaign.content,
    }, true);
    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return false;
    }

    dispatch(showSuccess(t('Email campaign updated')));
    return true;
  }

  function setCampaignInputField(key: keyof IEmailCampaign, max: number = 0) {
    return (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setState({
        ...state,
        campaign: {
          ...state.campaign,
          [key]: max > 0 ? e.target?.value.substring(0, max) : e.target?.value,
        },
      });
    };
  }

  function setCampaignSelectField(key: keyof IEmailCampaign) {
    return (value: number) => {
      if (key === 'audience_id' && value === -1) {
        history.push('/admin/marketing/email-campaigns/smart-list/new?campaign_id=' + state.campaign.id);
      }
      setState({
        ...state,
        campaign: {
          ...state.campaign,
          [key]: value,
        },
      });
    };
  }

  function setCampaignStringField(key: keyof IPageState['campaign']) {
    return (value: string) => {
      setState({
        ...state,
        campaign: {
          ...state.campaign,
          [key]: value,
        },
      });
    };
  }

  function setCampaignRadioField(key: keyof IEmailCampaign) {
    return (value: number | string | Record<any, string>) => {
      setState({
        ...state,
        campaign: {
          ...state.campaign,
          [key]: value,
        },
      });
    };
  }

  function setCampaignDateField(key: keyof IPageState['campaign']) {
    return (value: Date) => {
      setState({
        ...state,
        campaign: {
          ...state.campaign,
          [key]: moment(value).format('YYYY-MM-DD'),
        },
      });
    };
  }

  async function loadAudiences() {
    const resp = await GetAudiences(null, false);

    setAudiences(resp.data);
  }

  async function loadCampaign() {
    if (!isEdit) {
      return;
    }

    const resp = await GetEmailCampaigns({ id: campaignId }, false);
    if (resp.status === StatusCode.OK) {
      if (resp.data.length === 0) {
        dispatch(showError('Could not find email campaign'));
        history.push('/admin/marketing/email-campaigns');
        return;
      }
      const campaign: IPageState['campaign'] = resp.data.pop();
      if (campaign.scheduled_at != null) {
        const scheduledAt = moment(campaign.scheduled_at);
        campaign.scheduled_date = scheduledAt.format('YYYY-MM-DD');
        campaign.scheduled_time = scheduledAt.format('HH:mm');
      }

      setState(prev => ({
        ...prev,
        campaign: {
          ...state.campaign,
          ...campaign,
        },
      }));
    }
  }

  const [customerTableState, setCustomerTableState] = useState({
    limit: 15,
    offset: 0,
    search: '',
  });

  async function loadAudienceCustomers() {
    if (state.campaign.audience_id == 0 || state.campaign.audience_id == null) {
      return;
    }

    const page = Math.floor(customerTableState.offset / customerTableState.limit) + 1;

    setLoading(true);
    const resp = await GetAudienceCustomers({
      id: state.campaign.audience_id,
      campaign_id: state.campaign.id,
      page,
      search: customerTableState.search,
    }, false);
    setLoading(false);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    setAudienceCustomers(resp.data.customers.data);
    setCounts({
      excludedCount: resp.data.excluded_count,
      includedCount: resp.data.included_count,
      total: resp.data.customers.total,
    });
  }

  useEffect(() => {
    void loadAudienceCustomers();
  }, [state.campaign.audience_id, customerTableState]);

  useEffect(() => {
    void loadAudiences();
    void loadCampaign();
  }, [campaignId]);

  const recurringScheduleLabels = {
    recurring_weekly: t('Every 7 days'),
    recurring_monthly: t('Every Month'),
    recurring_biannually: t('Every 6 Month'),
    recurring_annually: t('Every Year'),
    recurring_birthday: t('Customer Birthday'),
  };
  const tabs = [
    {
      id: TabType.Details,
      content: t('Campaign'),
    },
    {
      id: TabType.Audience,
      content: t('Smart List'),
    },
    {
      id: TabType.Preview,
      content: t('Preview'),
    },
  ];

  const audience = audiences.filter(a => a.id === state.campaign.audience_id)[0];
  const accordionItems = [
    {
      badge: t('Step 1'),
      success_badge: audience?.id > 0,
      title: t('Choose a smart list'),
      success_preview: <div className="text-sm text-gray-600">
        {audience?.title ?? 'All customers'}
      </div>,
      onSave: () => {
        if (state.campaign.audience_id == null) {
          dispatch(showError(t('A smart list is required.')));
          return false;
        }

        void updateStep2();
        return true;
      },
      content: <FormLayout>
        <FormLayout.Group>
          <Select
            label={t("Send to")}
            defaultValue={state.campaign?.audience_id}
            onChange={setCampaignSelectField('audience_id')}
          >
            {audiences.map((audience, i) =>
              <Option key={i} value={audience.id} name={audience.title}>
                <span>
                  {audience.title}
                </span>
              </Option>,
            )}
            <Option value={-1} name="new">
              <span>
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                {t('New Smart List')}
              </span>
            </Option>
          </Select>
        </FormLayout.Group>
        {audience?.id != null && <FormLayout.Group>
          <div className="text-sm">
            <span className="font-medium">{audience.included_customers_count}</span> {t('customers matched in the smart list')} • <span className="font-medium cursor-pointer" onClick={() => setSegmentModalOpen(true)}>{t('View Customers')}</span>
          </div>
        </FormLayout.Group>}
      </FormLayout>,
    },
    {
      badge: t('Step 2'),
      success_badge: state.campaign?.from_address?.length > 0 && state.campaign?.from_name?.length > 0,
      success_preview: <div className="text-sm text-gray-600">
        {state.campaign.from_name}, {state.campaign.from_address}{'@teeon.com'}
      </div>,
      title: t('From'),
      onSave: () => {
        if (state.campaign?.from_name == null || state.campaign.from_name?.length < 2) {
          dispatch(showError(t('From name is required.')));
          return false;
        }
        void updateStep2();
        return true;
      },
      content: <FormLayout>
        <div className="grid grid-cols-2">
          <FormLayout.Group>
            <Input
              label={t("From Name")}
              placeholder={t("Name")}
              value={state.campaign?.from_name ?? ''}
              onChange={setCampaignInputField("from_name")}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <Input
              className="text-right"
              label={t("Email")}
              placeholder={t("info")}
              value={state.campaign?.from_address ?? ''}
              onChange={setCampaignInputField("from_address")}
              suffix={'@teeon.com'}
            />
          </FormLayout.Group>
        </div>
      </FormLayout>,
    },
    {
      badge: t('Step 3'),
      success_badge: state.campaign?.subject?.length > 0,
      success_preview: <div className="text-sm text-gray-600">
        <span className="font-medium">{t('Subject line')}:</span> {state.campaign.subject} • <span className="font-medium">{t('Preview text')}:</span> {state.campaign.text_preview}
      </div>,
      title: t('Subject and preview text'),
      onSave: () => {
        if (state.campaign.subject == null || state.campaign.subject.length < 1) {
          dispatch(showError(t('A subject is required.')));
          return false;
        }

        void updateStep2();
        return true;
      },
      content: <FormLayout>
        <FormLayout.Group>
          <Input
            label={t("Subject")}
            placeholder={t("Subject")}
            value={state.campaign?.subject ?? ''}
            onChange={setCampaignInputField("subject")}
          />
        </FormLayout.Group>
        <FormLayout.Group>
          <Input
            label={t("Text Preview")}
            placeholder={t("Text Preview")}
            value={state.campaign?.text_preview ?? ''}
            onChange={setCampaignInputField("text_preview")}
          />
        </FormLayout.Group>
      </FormLayout>,
    },
    {
      badge: t('Step 4'),
      success_badge: state.campaign?.scheduled_type === 'now'
        || (state.campaign?.scheduled_type === 'later' && state.campaign?.scheduled_date !== '' && state.campaign?.scheduled_time !== '')
        || state.campaign.scheduled_type === 'recurring' && state.campaign.scheduled_recurring_type != null
      ,
      success_preview: <div className="text-sm text-gray-600">
        <span className="font-medium">{t('For')}: </span>
        {state.campaign.scheduled_type === 'now' && 'now'}
        {state.campaign.scheduled_type === 'recurring' && recurringScheduleLabels[state.campaign.scheduled_recurring_type]}
        {state.campaign.scheduled_type === 'later' &&
          moment(state.campaign.scheduled_date + ' ' + state.campaign.scheduled_time).format('MMMM D, YYYY [at] h:mm a')
        }
      </div>,
      title: t('Schedule time'),
      onSave: () => {
        if (state.campaign?.scheduled_type === 'later') {
          if (state.campaign.scheduled_date == null || state.campaign.scheduled_date == '') {
            dispatch(showError(t('A scheduled date is required.')));
            return false;
          } else if (state.campaign.scheduled_time == null || state.campaign.scheduled_time == '') {
            dispatch(showError(t('A scheduled time is required.')));
            return false;
          }
        }

        void updateStep2();
        return true;
      },
      content: <FormLayout>
        <FormLayout.Group>
          <div className="grid grid-cols-3 gap-y-4">
            <div className="col-span-3">
              <Radio.Group
                name="number_of_golfers"
                onChange={setCampaignRadioField('scheduled_type')}
                value={state.campaign?.scheduled_type}
              >
                <Radio.Button value={'now'}>
                  {t('Schedule Now')}
                </Radio.Button>
                <Radio.Button value={'later'}>
                  {t('Schedule Later')}
                </Radio.Button>
              </Radio.Group>
            </div>
            {state.campaign?.scheduled_type === 'later' && <div className="col-span-3 grid grid-cols-2 gap-4">
              <DatePickerInput
                months={1}
                position="center"
                startingDate={new Date((new Date(state.campaign?.scheduled_date ?? null)).getTime() + (new Date()).getTimezoneOffset() * 60 * 1000)}
                setStartingDate={setCampaignDateField('scheduled_date')}
                disablePastDays
              />
              <TimePick
                value={state.campaign?.scheduled_time}
                onChange={setCampaignStringField('scheduled_time')}
                align="right"
              />
            </div>}
            {/* {state.campaign?.scheduled_type.startsWith('recurring') && <div className="col-span-3 grid grid-cols-3 gap-4"> */}
            {/*   <div className="col-span-2"></div> */}
            {/*   <div> */}
            {/*     <SelectNew */}
            {/*       id="recurring_type" */}
            {/*       placeholder={t("Send every...")} */}
            {/*       value={state.campaign.scheduled_recurring_type} */}
            {/*       onChange={setCampaignStringField('scheduled_recurring_type')} */}
            {/*       options={Object.entries(recurringScheduleLabels).map(([t, label]) => */}
            {/*       ({ */}
            {/*         id: t, */}
            {/*         label, */}
            {/*       }), */}
            {/*       )} */}
            {/*     /> */}
            {/*   </div> */}
            {/* </div>} */}
          </div>
        </FormLayout.Group>
      </FormLayout>,
    },
  ];

  async function duplicate() {
    const resp = await PostCampaignDuplicate({ id: state.campaign.id });

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    dispatch(showSuccess(t('Duplicated email campaign')));
    history.push('/admin/marketing/email-campaigns/' + resp.data.id);
    void loadCampaign();
  }

  const backUrl = '/admin/marketing/email-marketing';

  function canEditCampaign() {
    return state.campaign.published_at == null;
  }

  function handlePageChange(dir: 'next' | 'prev', limit: number, offset: number) {
    if (dir === 'next') {
      setCustomerTableState({ ...customerTableState, offset: offset + limit });
    } else {
      setCustomerTableState({ ...customerTableState, offset: offset - limit });
    }
  }

  function handleSearch(search: string) {
    setCustomerTableState({ ...customerTableState, offset: 0, search });
  }

  const [editEmailContent, setEditEmailContent] = useState(false);

  function handleSaveEmailContent() {
    setEditEmailContent(false);
    void updateStep3();
  }


  function handleImport(newCustomers: IAudienceCustomer[], importType: 'append' | 'replace') {
    const newList = importType === 'append' && audience?.imported_customer_ids != null
      ? audience?.imported_customer_ids.concat(newCustomers.map(c => c.id))
      : newCustomers.map(c => c.id);

    void updateAudience(_.uniq(newList));
  }

  async function handleExport() {
    const resp = await GetAudienceCustomersExport({ id: audience.id });
    downloadCsv(resp.data, _.snakeCase(audience.title) + '.csv');
  }

  async function updateAudience(imported_customer_ids: number[]) {
    const resp = await PutAudience({
      ...audience,
      imported_customer_ids,
      removed_customer_ids: [],
    }, false);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    dispatch(showSuccess(t('Updated smart list')));


    await loadAudienceCustomers();
  }

  const [editTitle, setEditTitle] = useState(false);

  async function updateTitle() {
    await updateStep1();
    setEditTitle(false);
  }

  function customerStatus(customer: IAudienceCustomer): IAudienceCustomer['status'] {
    if (!customer.preference_marketing_email) {
      return 'unsubscribed';
    }

    if (customer.excluded) {
      return 'excluded';
    }

    return 'active';
  }

  function handleIncludeToggle(customer: IAudienceCustomer) {
    let updated: IAudienceCustomer[] = audienceCustomers;

    const targetIndex = updated.findIndex(c => customer.id === c.id);
    const excluded = !customer.excluded;
    const updatedCustomer: IAudienceCustomer = {
      ...customer,
      excluded: excluded,
    };

    updatedCustomer.status = customerStatus(updatedCustomer);

    updated = updated.slice(0, targetIndex)
      .concat([updatedCustomer])
      .concat(updated.slice(targetIndex + 1));

    setAudienceCustomers(updated);
    setCounts({
      ...counts,
      includedCount: excluded ? counts.includedCount - 1 : counts.includedCount + 1,
    });
    setState({
      ...state,
      campaign: {
        ...state.campaign,
      },
    });

    void saveCustomerExcluded(customer.id, excluded);
  }

  async function saveCustomerExcluded(customer_id: number, excluded: boolean) {
    const resp = await PostCustomer({
      id: state.campaign.id,
      customer_id,
      excluded,
    });
    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));

      return;
    }
    dispatch(showSuccess(t('Updated smart list')));

  }

  async function deleteCampaign() {
    const resp = await DeleteCampaign({ id: state.campaign.id });

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    dispatch(showSuccess(t('Deleted email campaign')));
    history.push('/admin/marketing/email-marketing');
  }

  return <Page
    title={editTitle ?
      <div className="w-full font-semibold">
        <Input
          className="bg-white"
          label={t("Campaign Name")}
          placeholder={t("i.e. Men's League 2025")}
          value={state.campaign?.name}
          onChange={setCampaignInputField("name", 100)}
        />

        <div className="text-sm text-gray-600 mt-1.5">{state.campaign.name.length}/100 {t("characters")}</div>
      </div>
      : <div>
        {state.campaign.name}
        <FontAwesomeIcon icon={faEdit} className="ml-2" onClick={() => setEditTitle(true)} />
      </div>
    }
    subtitle={<div>
      {editTitle ?
        <div>
          <div className="text-sm font-semibold text-gray-700 mb-1.5">{t('Description')}</div>
          <textarea
            className="bg-white outline-1 outline-gray-300 text-sm rounded-lg! w-full p-2 focus:outline-[#6888C5] focus:shadow-[0px_0px_0px_4px_rgba(213,227,239,1)]"
            rows={5}
            placeholder={t("Add a short description for this campaign")}
            onChange={setCampaignInputField('description', 300)}
            value={state.campaign.description}
          />
          <div className="text-sm text-gray-600">{state.campaign.description.length}/300 {t("characters")}</div>
          <div className="flex gap-2">
            <ButtonNew type="primary" className="mt-2" onClick={updateTitle}>{t('Save')}</ButtonNew>
          </div>
        </div>
        : state.campaign.description}
    </div>}
    primaryAction={canEditCampaign() && {
      content: state.campaign.scheduled_type === 'now' ? t('Publish') : t('Schedule'),
      action: publish,
    }}
    multipleActionDropdownAction={{
      label: t('Quick Actions'),
      dropdownProps: {
        alignment: 'right',
        options: [
          {
            label: t('Duplicate'),
            icon: 'copy',
            type: 'handler',
            handler: duplicate,
          },
          {
            label: t('Delete'),
            icon: 'trash',
            type: 'handler',
            handler: deleteCampaign,
          },
        ],
      },
    }}
    breadcrumbs={[{
      prefix: true,
      label: t('Back'),
      url: backUrl,
    }]}
  >
    <div className="flex justify-around border-b border-gray-200 mb-4">
      {tabs.map(t =>
        <div
          key={t.id}
          className={classNames('px-auto py-2 flex-auto text-center cursor-pointer', {
            'border-b-2': t.id === state.selectedTab,
          })}
          onClick={() => setState({ ...state, selectedTab: t.id })}>
          {t.content}
        </div>,
      )}
    </div>
    <Card>
      <Card.Section>
        <div className="">
          {state.selectedTab === TabType.Details && <>
            <div className="text-lg font-semibold mb-2">
              {t('Campaign Details')}
            </div>
            <div className="text-md text-gray-600 border-b border-b-gray-200 pb-3 mb-4">
              {t('Manage the basics—audience, sender, subject, and send time.')}
            </div>

            <Accordion items={accordionItems} />
          </>
          }

          <div className={classNames({
            hidden: state.selectedTab !== TabType.Audience,
          })}>
            <div className="text-lg font-semibold mb-2">
              {audience?.title ?? t('Smart List')} {t('details')}
            </div>
            <div className="text-md text-gray-600 border-b border-b-gray-200 pb-3 mb-4">
              {t('Manage the customers in this campaign\'s smart list')}
            </div>
            {state.campaign.audience_id && <AudienceCustomersTable
              customers={audienceCustomers}
              onPageChange={handlePageChange}
              onCustomerSearch={handleSearch}
              onImport={handleImport}
              onExport={handleExport}
              limit={customerTableState.limit}
              offset={customerTableState.offset}
              total={counts.total}
              includedCount={counts.includedCount}
              onIncludeToggle={handleIncludeToggle}
              loading={loading}
            />}
          </div>

          {state.selectedTab === TabType.Preview && <FormLayout>
            <FormLayout.Group>
              {editEmailContent
                ? <div>
                  <div className="flex gap-2 justify-end">
                    <ButtonNew type="primary" onClick={handleSaveEmailContent}>{t('Save')}</ButtonNew>
                  </div>
                  <TextEditor
                    markdownText={state.campaign?.content}
                    markdownTextOnChange={setCampaignStringField('content')}
                    label={t("Email body")}
                  />
                </div>

                : <div>
                  <ButtonNew className="ml-auto" type="default" onClick={() => setEditEmailContent(!editEmailContent)}>{t('Edit')}</ButtonNew>
                  <Markdown markdownText={state.campaign?.content} />
                </div>
              }
            </FormLayout.Group>
          </FormLayout>}
        </div>
      </Card.Section>
    </Card>
    <Sheet
      size="medium"
      title={audience?.title ?? t('All customers')}
      supportText={t("Customize your smart list with filters for your targeted list.")}
      onOk={() => setSegmentModalOpen(false)}
      onSecondary={audience == null ? undefined : () => history.push('/admin/marketing/email-campaigns/smart-list/' + audience.id)}
      secondaryText={t('Edit Smart List')}
      okText={t('Close')}
      open={segmentModalOpen}
    >
      <AudienceCustomersTable
        customers={audienceCustomers}
        onPageChange={handlePageChange}
        onCustomerSearch={handleSearch}
        limit={customerTableState.limit}
        offset={customerTableState.offset}
        total={counts.total}
        includedCount={counts.includedCount}
        selectable={false}
        loading={loading}
      />
    </Sheet>
  </Page>;
}
